import { isSystemDataSequenceId } from "building-types/dataSequences/isSystemDataSequenceId";
import type { Doc } from "building-types/doc";
import type { DocEntityHandle, OptionSetHandle } from "building-types/handles/docEntityHandleTypes";
import objectKeyCount from "common/utils/objectKeyCount";
import { isBooleanOptionSet } from "doc/options/optionSetBaseUtils";
import { isSystemContentTemplateId } from "./contentTemplateTools";
import {
  isBuiltInCipComponent,
  isBuiltInMasonryComponent,
  isBuiltInSlabFloorComponent,
  isBuiltInSheathingComponent,
} from "./defaults/assemblyComponentDefaults";
import { isBuiltInFrameFloorAssembly, isBuiltInSlabFloorAssembly } from "./defaults/defaultFloorAssembly";
import { docEntityIsReadonly } from "./docEntityReadonly";
import { docFoundationAssemblyHasMandatoryReferences } from "./docFoundationAssemblyDeletionGuard";
import { docMapBlockHandlesOnLevel } from "./docMapHandles";
import {
  docCountBlocksInWorld,
  docGetLevelWorldId,
  docGetBlockWorldId,
  docCountLevelsInWorld,
} from "./docWorldLevelCounting";
import { isBuiltInFramingComponent } from "./elements/framing/defaultFramingComponents";
import { isBuiltInHeaderAssembly } from "./elements/framing/defaultHeaderAssembly";
import { builtInPropAttributes } from "./elements/propCatalog/builtInProps";
import { isSystemDefaultProfile } from "./elements/trim/defaultTrimProfiles";
import { docEntityHandleToEntityRef } from "./entity/docEntityHandleToEntityRef";
import { isShapeOrShapeComponentHandle } from "./entity/docEntityHandleTools";
import { docGetTableEntity } from "./entity/docGetTableEntity";
import { isSystemOptionSet } from "./options/systemOptionSet";
import { isBuiltInLabel } from "./utils/builtInLabelUtils";
import { isBuiltInDatum } from "./utils/datumTools";

export function docEntityIsDeletable(doc: Doc, handle: DocEntityHandle): boolean {
  // if we're in shape edit mode
  if (isShapeOrShapeComponentHandle(handle) && handle.parent.type === "EDIT_MODE") {
    return true;
  }

  if (handle.type === "ELEVATION_VIEW" || handle.type === "EDIT_MODE") {
    return false;
  }

  if (handle.type === "PLAN_SET_TEMPLATE_CONFIG") {
    return !docEntityHandleToEntityRef(doc, handle);
  }

  if (docEntityIsReadonly(doc, handle, "DELETE")) {
    return false;
  }

  switch (handle.type) {
    case "LEVEL": {
      if (docCountLevelsInWorld(doc, docGetLevelWorldId(doc, handle.id)) <= 1) {
        return false;
      }
      const blocksInWorld = docCountBlocksInWorld(doc, docGetLevelWorldId(doc, handle.id));
      if (blocksInWorld > 0 && blocksInWorld === docMapBlockHandlesOnLevel(doc, handle.id).length) {
        return false;
      }
      return true;
    }
    case "BLOCK": {
      return docCountBlocksInWorld(doc, docGetBlockWorldId(doc, handle.id)) > 1;
    }
    case "OPTION": {
      const optionSetHandle: OptionSetHandle = { type: "OPTION_SET", id: handle.optionSetId };
      const optionSet = docGetTableEntity(doc, optionSetHandle);

      // don't allow deletion if it's the last option
      if (optionSet && objectKeyCount(optionSet.options) === 1) {
        return false;
      }

      // prevent deletion for boolean options
      if (optionSet && isBooleanOptionSet(optionSet)) {
        return false;
      }

      return docEntityIsDeletable(doc, optionSetHandle);
    }

    case "OPTION_SET": {
      return !isSystemOptionSet(handle);
    }

    case "PLAN_VIEW": {
      // Composite plan view deletion is handled by the composite level
      if (doc.planViews[handle.id]?.planViewType === "COMPOSITE") {
        return false;
      }

      return objectKeyCount(doc.planViews) > 1;
    }

    case "VIEW_3D": {
      return objectKeyCount(doc.view3Ds) > 1;
    }

    case "AUTO_DIMENSION": {
      return false;
    }

    case "EDGE":
    case "VERTEX": {
      return ["EDGE", "VERTEX"].indexOf(handle.type) === -1;
    }

    case "PROP": {
      return !builtInPropAttributes[handle.id];
    }
    case "SYSTEM_PARAMETER": {
      return false;
    }
    case "LABEL": {
      return !isBuiltInLabel(handle.id);
    }
    case "DATUM": {
      return !isBuiltInDatum(handle.id);
    }
    case "FRAMING_COMPONENT": {
      return !isBuiltInFramingComponent(handle.id);
    }
    case "HEADER_ASSEMBLY": {
      return !isBuiltInHeaderAssembly(handle.id);
    }
    case "DATA_SEQUENCE": {
      return !isSystemDataSequenceId(handle.id);
    }
    case "SLAB_COMPONENT": {
      return !isBuiltInSlabFloorComponent(handle.id);
    }
    case "SHEATHING_COMPONENT": {
      return !isBuiltInSheathingComponent(handle.id);
    }
    case "MASONRY_COMPONENT": {
      return !isBuiltInMasonryComponent(handle.id);
    }
    case "CIP_COMPONENT": {
      return !isBuiltInCipComponent(handle.id);
    }
    case "FRAME_FLOOR_ASSEMBLY": {
      return !isBuiltInFrameFloorAssembly(handle.id);
    }
    case "SLAB_FLOOR_ASSEMBLY": {
      return !isBuiltInSlabFloorAssembly(handle.id);
    }
    case "FOUNDATION_ASSEMBLY": {
      return !docFoundationAssemblyHasMandatoryReferences(doc, handle.id);
    }
    case "CONTENT_TEMPLATE": {
      return !isSystemContentTemplateId(handle.id);
    }
    case "PROFILE": {
      return !isSystemDefaultProfile(handle.id);
    }
    default: {
      return true;
    }
  }
}

/**
 * Can all of the entity's children be deleted at once?
 */
export function docEntityHasMassDeletableChildren(_doc: Doc, handle: DocEntityHandle): boolean {
  switch (handle.type) {
    case "GROUP":
    case "OPTION_SET": {
      return false;
    }
    default: {
      return true;
    }
  }
}
